import React, { useState, useEffect } from 'react';

const SupportApp = () => {
  // Set document title and favicon
  useEffect(() => {
    document.title = 'Daily Arcade - Support & Help Center';
    
    // Create favicon
    const favicon = document.querySelector("link[rel~='icon']");
    if (favicon) {
      favicon.href = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:%23ff6b6b"/><stop offset="100%" style="stop-color:%234ecdc4"/></linearGradient></defs><rect width="100" height="100" rx="20" fill="url(%23grad)"/><text x="50" y="65" font-size="60" text-anchor="middle" fill="white">🎮</text></svg>';
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:%23ff6b6b"/><stop offset="100%" style="stop-color:%234ecdc4"/></linearGradient></defs><rect width="100" height="100" rx="20" fill="url(%23grad)"/><text x="50" y="65" font-size="60" text-anchor="middle" fill="white">🎮</text></svg>';
      document.head.appendChild(link);
    }
  }, []);
  const [visibleElements, setVisibleElements] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleElements(prev => new Set(prev.add(entry.target.id)));
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    const elements = document.querySelectorAll('.fade-in');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const targetPosition = element.offsetTop - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="app">
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
          color: #333;
          overflow-x: hidden;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .header {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          color: white;
          padding: 1rem 0;
          position: fixed;
          width: 100%;
          top: 0;
          z-index: 1000;
          backdrop-filter: blur(10px);
        }

        .nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1.5rem;
          font-weight: bold;
        }

        .logo-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
        }

        .nav-links {
          display: flex;
          list-style: none;
          gap: 2rem;
        }

        .nav-link {
          color: white;
          text-decoration: none;
          transition: color 0.3s ease;
          cursor: pointer;
        }

        .nav-link:hover {
          color: #ff6b6b;
        }

        .hero {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          color: white;
          padding: 120px 0 80px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
                            radial-gradient(circle at 80% 20%, rgba(255, 107, 107, 0.3) 0%, transparent 50%),
                            radial-gradient(circle at 40% 80%, rgba(78, 205, 196, 0.3) 0%, transparent 50%);
          animation: float 6s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .hero-content {
          position: relative;
          z-index: 2;
        }

        .hero h1 {
          font-size: 3rem;
          margin-bottom: 1rem;
          animation: slideInUp 1s ease;
        }

        .hero p {
          font-size: 1.2rem;
          margin-bottom: 2rem;
          opacity: 0.9;
          animation: slideInUp 1s ease 0.2s both;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .store-badges {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-top: 2rem;
          flex-wrap: wrap;
        }

        .store-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255,255,255,0.1);
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          color: white;
          text-decoration: none;
          transition: all 0.3s ease;
          border: 1px solid rgba(255,255,255,0.2);
          cursor: pointer;
        }

        .store-badge:hover {
          background: rgba(255,255,255,0.2);
          transform: translateY(-2px);
        }

        .games-section {
          padding: 80px 0;
          background: #f8f9fa;
        }

        .games-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
        }

        .game-card {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .game-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #ff6b6b, #4ecdc4);
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }

        .game-card:hover::before {
          transform: scaleX(1);
        }

        .game-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }

        .game-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
          display: block;
        }

        .game-card h3 {
          font-size: 1.5rem;
          margin-bottom: 1rem;
          color: #333;
        }

        .game-card ul {
          margin-top: 1rem;
          padding-left: 1.5rem;
        }

        .faq-section {
          padding: 80px 0;
          background: white;
        }

        .faq-item {
          background: #f8f9fa;
          border-radius: 12px;
          margin-bottom: 1rem;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .faq-question {
          padding: 1.5rem;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 600;
          background: white;
          border: 1px solid #e9ecef;
          transition: all 0.3s ease;
        }

        .faq-question:hover {
          background: #f8f9fa;
        }

        .faq-answer {
          padding: 0 1.5rem;
          max-height: 0;
          overflow: hidden;
          transition: all 0.3s ease;
          background: #f8f9fa;
        }

        .faq-item.active .faq-answer {
          max-height: 200px;
          padding: 1.5rem;
        }

        .faq-icon {
          transition: transform 0.3s ease;
        }

        .faq-item.active .faq-icon {
          transform: rotate(180deg);
        }

        .contact-section {
          padding: 80px 0;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          color: white;
        }

        .contact-content {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 3rem;
          margin-top: 3rem;
        }

        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: rgba(255,255,255,0.1);
          border-radius: 12px;
          transition: background 0.3s ease;
        }

        .contact-item:hover {
          background: rgba(255,255,255,0.15);
        }

        .contact-icon {
          width: 50px;
          height: 50px;
          background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
        }

        .contact-form {
          background: rgba(255,255,255,0.1);
          padding: 2rem;
          border-radius: 16px;
          backdrop-filter: blur(10px);
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 1rem;
          border: none;
          border-radius: 8px;
          background: rgba(255,255,255,0.9);
          color: #333;
          font-size: 1rem;
        }

        .form-group textarea {
          height: 120px;
          resize: vertical;
        }

        .submit-btn {
          background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.3s ease;
        }

        .submit-btn:hover {
          transform: translateY(-2px);
        }

        .footer {
          background: #1a1a2e;
          color: white;
          padding: 2rem 0;
          text-align: center;
        }

        .section-title {
          text-align: center;
          font-size: 2.5rem;
          margin-bottom: 1rem;
          background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .section-subtitle {
          text-align: center;
          font-size: 1.1rem;
          color: #666;
          margin-bottom: 2rem;
        }

        .fade-in {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s ease;
        }

        .fade-in.visible {
          opacity: 1;
          transform: translateY(0);
        }

        @media (max-width: 768px) {
          .nav-links {
            display: none;
          }
          
          .hero h1 {
            font-size: 2rem;
          }
          
          .games-grid {
            grid-template-columns: 1fr;
          }
          
          .contact-content {
            grid-template-columns: 1fr;
          }

          .store-badges {
            flex-direction: column;
            align-items: center;
          }
        }
      `}</style>

      <Header scrollToSection={scrollToSection} />
      <HeroSection />
      <GamesSection visibleElements={visibleElements} />
      <FAQSection visibleElements={visibleElements} />
      <ContactSection visibleElements={visibleElements} />
      <Footer />
    </div>
  );
};

const Header = ({ scrollToSection }) => (
  <header className="header">
    <nav className="nav container">
      <div className="logo">
        <div className="logo-icon">🎮</div>
        <span>Daily Arcade</span>
      </div>
      <ul className="nav-links">
        <li><span className="nav-link" onClick={() => scrollToSection('games')}>Games</span></li>
        <li><span className="nav-link" onClick={() => scrollToSection('faq')}>FAQ</span></li>
        <li><span className="nav-link" onClick={() => scrollToSection('contact')}>Contact</span></li>
      </ul>
    </nav>
  </header>
);

const HeroSection = () => (
  <section className="hero">
    <div className="container">
      <div className="hero-content">
        <h1>Daily Arcade Support</h1>
        <p>Need help with our premium gaming experience? We're here to assist you.</p>
        <div className="store-badges">
          <div className="store-badge">
            <span>📱</span>
            <span>Download on App Store</span>
          </div>
          <div className="store-badge">
            <span>🤖</span>
            <span>Get it on Google Play</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const GamesSection = ({ visibleElements }) => (
  <section id="games" className="games-section">
    <div className="container">
      <h2 className={`section-title fade-in ${visibleElements.has('games-title') ? 'visible' : ''}`} id="games-title">
        Our Games
      </h2>
      <p className={`section-subtitle fade-in ${visibleElements.has('games-subtitle') ? 'visible' : ''}`} id="games-subtitle">
        Two premium games crafted for the ultimate mobile experience
      </p>
      
      <div className="games-grid">
        <GameCard
          icon="👑"
          title="Queens Puzzle"
          description="Strategic logic puzzle where you place queens on the board following simple rules: one queen per row, column, and colored region. No diagonal touches allowed. Features randomly generated regions and built-in timer for endless replayability."
          features={[
            "Logic-based gameplay",
            "Randomly generated puzzles", 
            "Built-in timer",
            "Multiple difficulty levels"
          ]}
          visible={visibleElements.has('game-queens')}
          id="game-queens"
        />
        
        <GameCard
          icon="🃏"
          title="Indian Rummy"
          description="Classic card game with smooth gameplay and intelligent AI opponent. Form sequences and sets, manage your hand strategically, and enjoy the timeless appeal of rummy optimized for mobile devices with landscape orientation."
          features={[
            "Play against smart AI",
            "Automatic card arrangement",
            "Custom group creation", 
            "Professional card design"
          ]}
          visible={visibleElements.has('game-rummy')}
          id="game-rummy"
        />
      </div>
    </div>
  </section>
);

const GameCard = ({ icon, title, description, features, visible, id }) => (
  <div className={`game-card fade-in ${visible ? 'visible' : ''}`} id={id}>
    <span className="game-icon">{icon}</span>
    <h3>{title}</h3>
    <p>{description}</p>
    <ul>
      {features.map((feature, index) => (
        <li key={index}>{feature}</li>
      ))}
    </ul>
  </div>
);

const FAQSection = ({ visibleElements }) => {
  const [activeFAQ, setActiveFAQ] = useState(null);
  
  const faqs = [
    {
      question: "How do I play Queens Puzzle?",
      answer: "Place queens on the board so that there's exactly one queen in each row, column, and colored region. Queens cannot touch diagonally. Tap to place/remove queens, long-press to add helper dots."
    },
    {
      question: "How do I play Indian Rummy?",
      answer: "Form valid sequences (consecutive cards of same suit) and sets (same value cards). Draw from deck or discard pile, then discard one card. Declare when you have valid groups. The app automatically arranges your cards by suit."
    },
    {
      question: "Why does the screen orientation change between games?",
      answer: "Each game is optimized for the best experience: Queens Puzzle works best in portrait mode for board visibility, while Indian Rummy uses landscape mode to display cards comfortably. The app automatically switches orientation."
    },
    {
      question: "Is internet connection required?",
      answer: "No! Daily Arcade works completely offline. All games, AI opponents, and features work without any internet connection, making it perfect for travel or areas with poor connectivity."
    },
    {
      question: "Are there in-app purchases or ads?",
      answer: "Daily Arcade is a premium experience with no ads and no in-app purchases. You get the complete gaming experience with your one-time purchase."
    },
    {
      question: "How do I create custom groups in Rummy?",
      answer: "Enable Multi-Select mode, select the cards you want to group together, then tap the 'Group' button. This is useful for creating sets or sequences that the auto-arrangement might not detect."
    }
  ];

  return (
    <section id="faq" className="faq-section">
      <div className="container">
        <h2 className={`section-title fade-in ${visibleElements.has('faq-title') ? 'visible' : ''}`} id="faq-title">
          Frequently Asked Questions
        </h2>
        <p className={`section-subtitle fade-in ${visibleElements.has('faq-subtitle') ? 'visible' : ''}`} id="faq-subtitle">
          Find answers to common questions about Daily Arcade
        </p>
        
        <div style={{ marginTop: '3rem' }}>
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isActive={activeFAQ === index}
              onClick={() => setActiveFAQ(activeFAQ === index ? null : index)}
              visible={visibleElements.has(`faq-${index}`)}
              id={`faq-${index}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQItem = ({ question, answer, isActive, onClick, visible, id }) => (
  <div className={`faq-item fade-in ${visible ? 'visible' : ''} ${isActive ? 'active' : ''}`} id={id}>
    <div className="faq-question" onClick={onClick}>
      <span>{question}</span>
      <span className="faq-icon">▼</span>
    </div>
    <div className="faq-answer">
      <p>{answer}</p>
    </div>
  </div>
);

const ContactSection = ({ visibleElements }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = () => {
    const { name, email, subject, message } = formData;
    const mailtoLink = `mailto:dailyarcade94@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`)}`;
    
    window.location.href = mailtoLink;
    
    alert('Thank you for your message! Your email client should open now. If not, please send your message directly to dailyarcade94@gmail.com');
    
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        <h2 className={`section-title fade-in ${visibleElements.has('contact-title') ? 'visible' : ''}`} id="contact-title">
          Get in Touch
        </h2>
        <p className={`section-subtitle fade-in ${visibleElements.has('contact-subtitle') ? 'visible' : ''}`} id="contact-subtitle" style={{ color: 'rgba(255,255,255,0.8)' }}>
          Need help? Have feedback? We'd love to hear from you!
        </p>
        
        <div className="contact-content">
          <div className={`contact-info fade-in ${visibleElements.has('contact-info') ? 'visible' : ''}`} id="contact-info">
            <div className="contact-item">
              <div className="contact-icon">📧</div>
              <div>
                <h3>Email Support</h3>
                <p>dailyarcade94@gmail.com</p>
                <small>We typically respond within 24 hours</small>
              </div>
            </div>
            
            <div className="contact-item">
              <div className="contact-icon">⏰</div>
              <div>
                <h3>Response Time</h3>
                <p>24-48 hours</p>
                <small>Monday to Friday, faster responses</small>
              </div>
            </div>
            
            <div className="contact-item">
              <div className="contact-icon">🌍</div>
              <div>
                <h3>Global Support</h3>
                <p>Worldwide</p>
                <small>Supporting players around the globe</small>
              </div>
            </div>
          </div>
          
          <div className={`contact-form fade-in ${visibleElements.has('contact-form') ? 'visible' : ''}`} id="contact-form">
            <h3 style={{ marginBottom: '1.5rem' }}>Send us a Message</h3>
            
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="subject">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us about your issue or feedback..."
              />
            </div>
            
            <button onClick={handleSubmit} className="submit-btn">Send Message</button>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="footer">
    <div className="container">
      <p>&copy; 2025 Daily Arcade. All rights reserved.</p>
      <p style={{ marginTop: '0.5rem', opacity: 0.8 }}>
        Made with care for mobile gaming enthusiasts worldwide
      </p>
    </div>
  </footer>
);

export default SupportApp;